import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/Collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Separator } from '@/components/ui/Separator';
import { Textarea } from '@/components/ui/Textarea';
import {
  CustomerSecretDrafts,
  CustomerSecretTarget,
  getConfigValueAtPath,
  getCustomerSecretDraft,
  hasMalformedVlmGatewaySection,
  hasMiscasedVlmGatewaySection,
  manageableCustomerSecretTargets,
  newVlmProfileId,
  VlmGatewayProfileTarget,
  vlmGatewayProfileTarget,
  vlmGatewayProfileTargetId,
  vlmGatewayProfileTargets,
  VlmProfileBlockedReason,
  vlmProfileRemovals,
} from '@/features/Customers/customerSecretStore';
import {
  editableVlmProfileNames,
  readVlmProfileSettings,
  validateNewVlmProfileName,
  validateVlmProfileSettings,
  VLM_PROVIDERS,
  VlmProfileSettingsField,
  VlmProfileSettingsForm,
} from '@/features/Customers/vlmGatewayProfileSettings';
import { cn } from '@/lib/utils';
import { ChevronRight, PenLine, Plus, Trash2, Undo2 } from 'lucide-react';
import { ReactNode, useEffect, useId, useMemo, useState } from 'react';
import { Control, useController } from 'react-hook-form';
import type {
  CustomerFormValues,
  SecretFieldOptions,
} from './CustomerDetailsForm';

const blockedReasonNotes: Record<VlmProfileBlockedReason, string> = {
  invalidId:
    "This profile's Id can't name a console-managed key. Use letters, digits and single dashes (40 characters at most), or remove the Id in JSON mode so the console gives it a new one.",
  idClash:
    'Another profile has the same Id, so the two would share one API key. If one is a copy, remove its Id and ApiKey in JSON mode, then set its key here.',
  nameClash:
    'Another profile has the same name in different letter case. The gateway refuses both until one is renamed or removed.',
  keyCasing:
    'This profile has a field spelled differently from what the console expects (for example apiKey instead of ApiKey). Fix the spelling in JSON mode to manage this profile here.',
};

const MISCASED_SECTION_NOTE =
  "The VLM gateway section isn't spelled VlmGateway.Profiles. Rename it in JSON mode to manage keys here.";

const MALFORMED_SECTION_NOTE =
  "The VlmGateway section or its Profiles isn't a JSON object. Fix it in JSON mode to manage profiles here.";

const NO_PROFILES_NOTE = 'No VLM gateway profiles.';

const INCOMPLETE_PROFILE_WARNING =
  'Calls to this profile fail until it has a provider and an API key.';

type SettingsFieldSpec = {
  field: VlmProfileSettingsField;
  label: string;
  control: 'provider' | 'text' | 'textarea' | 'json' | 'number';
  className: string;
  placeholder?: string;
  hint?: string;
};

const SETTINGS_FIELD_SPECS: readonly SettingsFieldSpec[] = [
  {
    field: 'Provider',
    label: 'Provider',
    control: 'provider',
    className: 'md:col-span-3',
  },
  {
    field: 'Model',
    label: 'Model',
    control: 'text',
    className: 'md:col-span-3',
  },
  {
    field: 'Prompt',
    label: 'Prompt',
    control: 'textarea',
    className: 'md:col-span-6',
    hint: 'Used when a call sends no prompt.',
  },
  {
    field: 'SystemPrompt',
    label: 'System prompt',
    control: 'textarea',
    className: 'md:col-span-6',
  },
  {
    field: 'ResponseSchema',
    label: 'Response schema',
    control: 'json',
    className: 'md:col-span-6',
    hint: 'JSON Schema for the answer. Used when a call sends no schema.',
  },
  {
    field: 'MaxTokens',
    label: 'Max tokens',
    control: 'number',
    className: 'md:col-span-2',
    placeholder: '8192',
  },
  {
    field: 'MaxImageDimension',
    label: 'Max image dimension',
    control: 'number',
    className: 'md:col-span-2',
    placeholder: 'No downscaling',
  },
  {
    field: 'TimeoutSeconds',
    label: 'Timeout (seconds)',
    control: 'number',
    className: 'md:col-span-2',
    placeholder: '60',
    hint: 'Keep it under 120, the ingress read timeout.',
  },
  {
    field: 'ProviderOptions',
    label: 'Provider options',
    control: 'json',
    className: 'md:col-span-6',
    hint: 'Merged into the provider request, for example thinking settings.',
  },
];

// Each new value opens the settings of the named profiles (after a blocked save).
export type VlmSettingsReveal = { profileNames: readonly string[] };

type RenderSecretField = (
  target: CustomerSecretTarget,
  inputId: string,
  label: string,
  options?: SecretFieldOptions,
) => ReactNode;

type VlmGatewayCardProps = {
  storedConfig: unknown;
  renderSecretField: RenderSecretField;
  control: Control<CustomerFormValues>;
  readOnly: boolean;
  secretDrafts: CustomerSecretDrafts;
  secretEditingAvailable: boolean;
  onDiscardSecretDraft?: (targetId: string) => void;
  settingsReveal?: VlmSettingsReveal;
};

const ProfileValue = ({ label, value }: { label: string; value: string }) => (
  <div className="grid gap-1">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className={cn(!value && 'text-muted-foreground')}>
      {value || 'Not set'}
    </dd>
  </div>
);

const SettingsControl = ({
  spec,
  id,
  value,
  error,
  describedBy,
  locked,
  onChange,
}: {
  spec: SettingsFieldSpec;
  id: string;
  value: string;
  error: string | undefined;
  describedBy: string | undefined;
  locked: boolean;
  onChange: (value: string) => void;
}) => {
  const ariaProps = {
    id,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy,
  };

  if (spec.control === 'provider') {
    const known = VLM_PROVIDERS.some((provider) => provider === value);
    return (
      <Select value={value} onValueChange={onChange} disabled={locked}>
        <SelectTrigger {...ariaProps} className="w-full">
          <SelectValue placeholder="Select provider" />
        </SelectTrigger>
        <SelectContent>
          {VLM_PROVIDERS.map((provider) => (
            <SelectItem key={provider} value={provider}>
              {provider}
            </SelectItem>
          ))}
          {/* Shows an unknown stored value instead of an empty select. */}
          {value && !known && (
            <SelectItem value={value} disabled>
              {value}
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    );
  }

  if (spec.control === 'textarea' || spec.control === 'json') {
    return (
      <Textarea
        {...ariaProps}
        rows={spec.control === 'json' ? 6 : 3}
        className={cn(spec.control === 'json' && 'font-mono text-xs')}
        readOnly={locked}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  // Numbers use text inputs: a number input reports unparseable text as empty,
  // which would read as "cleared" and delete the key instead of failing validation.
  return (
    <Input
      {...ariaProps}
      type="text"
      inputMode={spec.control === 'number' ? 'numeric' : undefined}
      placeholder={spec.placeholder}
      readOnly={locked}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
};

const VlmProfileSettings = ({
  form,
  errors,
  locked,
  reveal,
  defaultOpen,
  onChange,
}: {
  form: VlmProfileSettingsForm;
  errors: Partial<Record<VlmProfileSettingsField, string>>;
  locked: boolean;
  reveal: VlmSettingsReveal | undefined;
  defaultOpen: boolean;
  onChange: (field: VlmProfileSettingsField, value: string) => void;
}) => {
  const idPrefix = useId();
  const hasErrors = Object.keys(errors).length > 0;
  // Opens on stored errors so they are seen; the user can still collapse it.
  const [open, setOpen] = useState(hasErrors || defaultOpen);
  useEffect(() => {
    if (reveal && hasErrors) {
      setOpen(true);
    }
  }, [reveal, hasErrors]);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="group">
          <ChevronRight className="transition-transform group-data-[state=open]:rotate-90" />
          Settings
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="grid gap-4 pt-2 md:grid-cols-6">
        {SETTINGS_FIELD_SPECS.map((spec) => {
          const id = `${idPrefix}-${spec.field}`;
          const error = errors[spec.field];
          const describedBy =
            [spec.hint && `${id}-hint`, error && `${id}-error`]
              .filter(Boolean)
              .join(' ') || undefined;

          return (
            <div key={spec.field} className={cn('grid gap-2', spec.className)}>
              <Label htmlFor={id}>{spec.label}</Label>
              <SettingsControl
                spec={spec}
                id={id}
                value={form[spec.field]}
                error={error}
                describedBy={describedBy}
                locked={locked}
                onChange={(value) => onChange(spec.field, value)}
              />
              {spec.hint && (
                <p id={`${id}-hint`} className="text-muted-foreground text-sm">
                  {spec.hint}
                </p>
              )}
              {error && (
                <p id={`${id}-error`} className="text-destructive text-sm">
                  {error}
                </p>
              )}
            </div>
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
};

const RemoveProfileButton = ({
  name,
  onRemove,
}: {
  name: string;
  onRemove: () => void;
}) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    aria-label={`Remove ${name}`}
    onClick={onRemove}
  >
    <Trash2 />
    Remove
  </Button>
);

const RemovedProfileRow = ({
  name,
  deletesKey,
  readOnly,
  onUndo,
}: {
  name: string;
  deletesKey: boolean;
  readOnly: boolean;
  onUndo: () => void;
}) => (
  <div
    role="group"
    aria-label={name}
    className="grid gap-2 rounded-lg border border-dashed p-4"
  >
    <div className="flex items-center gap-2">
      <Badge variant="outline" className="line-through">
        {name}
      </Badge>
      <Separator className="flex-1" />
      {!readOnly && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={`Undo removing ${name}`}
          onClick={onUndo}
        >
          <Undo2 />
          Undo
        </Button>
      )}
    </div>
    <p className="text-muted-foreground text-sm">
      {deletesKey
        ? 'This profile and its API key will be removed when Save is clicked.'
        : 'This profile will be removed when Save is clicked.'}
    </p>
  </div>
);

type ProfileNames = {
  existing: readonly string[];
  removing: readonly string[];
};

// Lives inside the dialog content, so closing the dialog clears it.
const AddProfileForm = ({
  names,
  onAdd,
  onClose,
}: {
  names: ProfileNames;
  onAdd: (name: string, provider: string, model: string) => void;
  onClose: () => void;
}) => {
  const idPrefix = useId();
  const [name, setName] = useState('');
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const nameError = validateNewVlmProfileName(name.trim(), names);
  const providerError = provider === '' ? 'Select a provider.' : undefined;
  const ids = {
    name: `${idPrefix}-name`,
    provider: `${idPrefix}-provider`,
    model: `${idPrefix}-model`,
  };

  const handleAdd = () => {
    setSubmitted(true);
    if (nameError || providerError) {
      return;
    }
    onAdd(name.trim(), provider, model.trim());
    onClose();
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add VLM gateway profile</DialogTitle>
        <DialogDescription>
          The profile is created when you save the customer. Set its API key and
          other settings on the new profile before saving.
        </DialogDescription>
      </DialogHeader>
      <div className="grid gap-4">
        <NameField
          id={ids.name}
          value={name}
          error={submitted ? nameError : undefined}
          onChange={setName}
        />
        <div className="grid gap-2">
          <Label htmlFor={ids.provider}>Provider</Label>
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger
              id={ids.provider}
              className="w-full"
              aria-invalid={submitted && providerError ? true : undefined}
            >
              <SelectValue placeholder="Select provider" />
            </SelectTrigger>
            <SelectContent>
              {VLM_PROVIDERS.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {submitted && providerError && (
            <p className="text-destructive text-sm">{providerError}</p>
          )}
        </div>
        <div className="grid gap-2">
          <Label htmlFor={ids.model}>Model</Label>
          <Input
            id={ids.model}
            type="text"
            value={model}
            onChange={(event) => setModel(event.target.value)}
          />
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" onClick={handleAdd}>
          Add
        </Button>
      </DialogFooter>
    </>
  );
};

const AddProfileDialog = ({
  open,
  onOpenChange,
  names,
  onAdd,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  names: ProfileNames;
  onAdd: (name: string, provider: string, model: string) => void;
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <AddProfileForm
        names={names}
        onAdd={onAdd}
        onClose={() => onOpenChange(false)}
      />
    </DialogContent>
  </Dialog>
);

const NameField = ({
  id,
  value,
  error,
  onChange,
}: {
  id: string;
  value: string;
  error: string | undefined;
  onChange: (value: string) => void;
}) => (
  <div className="grid gap-2">
    <Label htmlFor={id}>Name</Label>
    <Input
      id={id}
      type="text"
      value={value}
      aria-invalid={error ? true : undefined}
      aria-describedby={`${id}-hint`}
      onChange={(event) => onChange(event.target.value)}
    />
    <p
      id={`${id}-hint`}
      className={cn(
        'text-sm',
        error ? 'text-destructive' : 'text-muted-foreground',
      )}
    >
      {error ??
        'Callers pass this name. Letters, digits and single dashes, 40 characters at most.'}
    </p>
  </div>
);

// Lives inside the dialog content, so closing the dialog clears it.
const RenameProfileForm = ({
  from,
  names,
  onRename,
  onClose,
}: {
  from: string;
  names: ProfileNames;
  onRename: (to: string) => void;
  onClose: () => void;
}) => {
  const id = useId();
  const [name, setName] = useState(from);
  const [submitted, setSubmitted] = useState(false);
  const trimmed = name.trim();
  const error =
    trimmed === from ? undefined : validateNewVlmProfileName(trimmed, names);

  const handleRename = () => {
    setSubmitted(true);
    if (error) {
      return;
    }
    onRename(trimmed);
    onClose();
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Rename {from}</DialogTitle>
        <DialogDescription>
          Callers that pass "{from}" fail until they use the new name. The API
          key stays with the profile.
        </DialogDescription>
      </DialogHeader>
      <NameField
        id={id}
        value={name}
        error={submitted ? error : undefined}
        onChange={setName}
      />
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" onClick={handleRename}>
          Rename
        </Button>
      </DialogFooter>
    </>
  );
};

const RenameProfileDialog = ({
  from,
  names,
  onRename,
  onClose,
}: {
  from: string | undefined;
  names: ProfileNames;
  onRename: (from: string, to: string) => void;
  onClose: () => void;
}) => (
  <Dialog open={from !== undefined} onOpenChange={(open) => !open && onClose()}>
    <DialogContent>
      {from !== undefined && (
        <RenameProfileForm
          from={from}
          names={names}
          onRename={(to) => onRename(from, to)}
          onClose={onClose}
        />
      )}
    </DialogContent>
  </Dialog>
);

type ProfileRename = {
  to: string;
  onUndo: () => void;
};

const VlmGatewayProfileRow = ({
  target,
  isNew,
  rename,
  renderSecretField,
  apiKeyManageable,
  settingsEditable,
  readOnly,
  hasApiKey,
  form,
  settingsReveal,
  onSettingsChange,
  onRename,
  onRemove,
}: {
  target: VlmGatewayProfileTarget;
  isNew: boolean;
  rename: ProfileRename | undefined;
  renderSecretField: RenderSecretField;
  apiKeyManageable: boolean;
  settingsEditable: boolean;
  readOnly: boolean;
  hasApiKey: boolean;
  form: VlmProfileSettingsForm;
  settingsReveal: VlmSettingsReveal | undefined;
  onSettingsChange: (field: VlmProfileSettingsField, value: string) => void;
  // Unset when the profile can't be renamed or removed here.
  onRename: (() => void) | undefined;
  onRemove: (() => void) | undefined;
}) => {
  const errors = settingsEditable ? validateVlmProfileSettings(form) : {};
  const incomplete =
    settingsEditable && (form.Provider.trim() === '' || !hasApiKey);
  const name = rename?.to ?? target.profileName;

  return (
    <div
      role="group"
      aria-label={name}
      className="grid gap-4 rounded-lg border p-4"
    >
      <div className="flex items-center gap-2">
        <Badge variant="secondary">{name}</Badge>
        {isNew && <Badge variant="outline">New</Badge>}
        {rename && (
          <span className="text-muted-foreground text-sm">
            renamed from {target.profileName}
          </span>
        )}
        <Separator className="flex-1" />
        {!readOnly && rename && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={`Undo renaming ${target.profileName}`}
            onClick={rename.onUndo}
          >
            <Undo2 />
            Undo
          </Button>
        )}
        {!readOnly && !rename && onRename && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={`Rename ${name}`}
            onClick={onRename}
          >
            <PenLine />
            Rename
          </Button>
        )}
        {!readOnly && onRemove && (
          <RemoveProfileButton name={name} onRemove={onRemove} />
        )}
      </div>
      <dl className="grid gap-4 text-sm sm:grid-cols-2">
        <ProfileValue label="Provider" value={target.provider} />
        <ProfileValue label="Model" value={target.model} />
      </dl>
      {renderSecretField(
        target,
        `vlm-gateway-${target.profileName}-api-key`,
        'API Key',
        { disabled: !apiKeyManageable },
      )}
      {target.blockedReason && (
        <p className="text-muted-foreground text-sm">
          {blockedReasonNotes[target.blockedReason]}
        </p>
      )}
      {incomplete && (
        <p className="text-orange text-sm">{INCOMPLETE_PROFILE_WARNING}</p>
      )}
      <VlmProfileSettings
        form={form}
        errors={errors}
        locked={readOnly || !settingsEditable}
        reveal={
          settingsReveal?.profileNames.includes(target.profileName)
            ? settingsReveal
            : undefined
        }
        defaultOpen={isNew}
        onChange={onSettingsChange}
      />
    </div>
  );
};

export const VlmGatewayCard = ({
  storedConfig,
  renderSecretField,
  control,
  readOnly,
  secretDrafts,
  secretEditingAvailable,
  onDiscardSecretDraft,
  settingsReveal,
}: VlmGatewayCardProps) => {
  const storedTargets = useMemo(
    () => vlmGatewayProfileTargets(storedConfig),
    [storedConfig],
  );
  // The save uses these same store results, so the card can't drift from it.
  const manageableIds = useMemo(
    () =>
      new Set(
        manageableCustomerSecretTargets(storedConfig).map(
          (target) => target.id,
        ),
      ),
    [storedConfig],
  );
  const editableNames = useMemo(
    () => editableVlmProfileNames(storedConfig),
    [storedConfig],
  );
  const keyedNames = useMemo(
    () =>
      new Set(
        vlmProfileRemovals(
          storedConfig,
          storedTargets.map((target) => target.profileName),
        )
          .filter((removal) => removal.keyTarget)
          .map((removal) => removal.name),
      ),
    [storedConfig, storedTargets],
  );
  const sectionNote = hasMiscasedVlmGatewaySection(storedConfig)
    ? MISCASED_SECTION_NOTE
    : hasMalformedVlmGatewaySection(storedConfig)
      ? MALFORMED_SECTION_NOTE
      : undefined;
  const { field } = useController({ control, name: 'vlmProfiles' });
  const { field: removedField } = useController({
    control,
    name: 'vlmRemovedProfiles',
  });
  const { field: renamedField } = useController({
    control,
    name: 'vlmRenamedProfiles',
  });
  const { field: addedIdsField } = useController({
    control,
    name: 'vlmAddedProfileIds',
  });
  const [addOpen, setAddOpen] = useState(false);
  const [renaming, setRenaming] = useState<string>();
  const forms = field.value ?? {};
  const removed = removedField.value ?? [];
  const renamed = renamedField.value ?? {};
  const addedIds = addedIdsField.value ?? {};
  const storedNames = new Set(storedTargets.map((t) => t.profileName));
  const addedNames = Object.keys(forms)
    .filter((name) => !storedNames.has(name))
    .sort((a, b) => a.localeCompare(b, 'en'));

  const hasStoredApiKey = (target: VlmGatewayProfileTarget): boolean =>
    getConfigValueAtPath(storedConfig, target.configPath).trim() !== '';

  const hasApiKey = (target: VlmGatewayProfileTarget): boolean => {
    const draft = getCustomerSecretDraft(secretDrafts, target.id);
    return draft.action === 'none'
      ? hasStoredApiKey(target)
      : draft.action === 'upsert';
  };

  // Passed as an event: a profile named "target" would otherwise be read as one.
  const setForms = (next: Record<string, VlmProfileSettingsForm>) =>
    field.onChange({ target: { value: next } });

  const setSetting = (
    name: string,
    settingsField: VlmProfileSettingsField,
    value: string,
  ) =>
    setForms({
      ...forms,
      [name]: {
        ...(forms[name] ?? readVlmProfileSettings(undefined)),
        [settingsField]: value,
      },
    });

  // Passed as an event: a profile named "target" would otherwise be read as one.
  const setAddedIds = (next: Record<string, string>) =>
    addedIdsField.onChange({ target: { value: next } });

  // The id is made now, so a key staged for the new profile is keyed by it.
  const addProfile = (name: string, provider: string, model: string) => {
    setForms({
      ...forms,
      [name]: {
        ...readVlmProfileSettings(undefined),
        Provider: provider,
        Model: model,
      },
    });
    setAddedIds({ ...addedIds, [name]: newVlmProfileId() });
  };

  const discardAddedProfile = (name: string) => {
    const { [name]: _discarded, ...rest } = forms;
    setForms(rest);
    if (Object.hasOwn(addedIds, name)) {
      const { [name]: id, ...otherIds } = addedIds;
      setAddedIds(otherIds);
      onDiscardSecretDraft?.(vlmGatewayProfileTargetId(id));
    }
  };

  // Passed as an event: a profile named "target" would otherwise be read as one.
  const setRenamed = (next: Record<string, string>) =>
    renamedField.onChange({ target: { value: next } });

  // Staged key changes are keyed by the profile's Id, so they follow a rename.
  const renameProfile = (from: string, to: string) => {
    if (to === from) {
      return;
    }
    setRenamed({ ...renamed, [from]: to });
  };

  const undoRename = (from: string) => {
    const { [from]: _undone, ...rest } = renamed;
    setRenamed(rest);
  };

  const removeStoredProfile = (name: string) => {
    undoRename(name);
    removedField.onChange([...removed, name]);
  };

  const renamedTo = (name: string) =>
    Object.hasOwn(renamed, name) ? renamed[name] : undefined;

  // Names stay reserved until the save, in case a rename or removal fails.
  const takenNames = [...storedNames, ...Object.values(renamed), ...addedNames];

  const formFor = (name: string) =>
    forms[name] ?? readVlmProfileSettings(undefined);

  return (
    <div className="flex flex-col gap-4 pt-2">
      {sectionNote ? (
        <p className="text-muted-foreground text-sm">{sectionNote}</p>
      ) : (
        storedTargets.length + addedNames.length === 0 && (
          <p className="text-muted-foreground text-sm">{NO_PROFILES_NOTE}</p>
        )
      )}
      {!sectionNote &&
        storedTargets.map((target) => {
          const renamedName = renamedTo(target.profileName);
          return removed.includes(target.profileName) ? (
            <RemovedProfileRow
              key={target.profileName}
              name={target.profileName}
              deletesKey={keyedNames.has(target.profileName)}
              readOnly={readOnly}
              onUndo={() =>
                removedField.onChange(
                  removed.filter((name) => name !== target.profileName),
                )
              }
            />
          ) : (
            <VlmGatewayProfileRow
              key={target.profileName}
              target={target}
              isNew={false}
              rename={
                renamedName === undefined
                  ? undefined
                  : {
                      to: renamedName,
                      onUndo: () => undoRename(target.profileName),
                    }
              }
              renderSecretField={renderSecretField}
              apiKeyManageable={manageableIds.has(target.id)}
              settingsEditable={editableNames.has(target.profileName)}
              readOnly={readOnly}
              hasApiKey={hasApiKey(target)}
              form={formFor(target.profileName)}
              settingsReveal={settingsReveal}
              onSettingsChange={(settingsField, value) =>
                setSetting(target.profileName, settingsField, value)
              }
              onRename={
                editableNames.has(target.profileName)
                  ? () => setRenaming(target.profileName)
                  : undefined
              }
              // Deleting its key needs senhub, so keep it until that works.
              onRemove={
                secretEditingAvailable || !keyedNames.has(target.profileName)
                  ? () => removeStoredProfile(target.profileName)
                  : undefined
              }
            />
          );
        })}
      {!sectionNote &&
        addedNames.map((name) => {
          const form = formFor(name);
          const target = vlmGatewayProfileTarget(
            name,
            { Id: addedIds[name], Provider: form.Provider, Model: form.Model },
            null,
          );
          return (
            <VlmGatewayProfileRow
              key={name}
              target={target}
              isNew
              rename={undefined}
              renderSecretField={renderSecretField}
              apiKeyManageable
              settingsEditable
              readOnly={readOnly}
              hasApiKey={hasApiKey(target)}
              form={form}
              settingsReveal={settingsReveal}
              onSettingsChange={(settingsField, value) =>
                setSetting(name, settingsField, value)
              }
              onRename={undefined}
              onRemove={() => discardAddedProfile(name)}
            />
          );
        })}
      {!sectionNote && !readOnly && (
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAddOpen(true)}
          >
            <Plus />
            Add profile
          </Button>
        </div>
      )}
      <AddProfileDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        names={{ existing: takenNames, removing: removed }}
        onAdd={addProfile}
      />
      <RenameProfileDialog
        from={renaming}
        names={{
          existing: takenNames.filter((name) => name !== renaming),
          removing: removed,
        }}
        onRename={renameProfile}
        onClose={() => setRenaming(undefined)}
      />
    </div>
  );
};
